import { useCallback, useEffect, useId, useRef } from 'react';
import { useNavigate } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSession } from '@/stores/session';
import { toast } from '@/stores/toasts';
import { useLocale } from '@/i18n/useLocale';
import { useFormat } from '@/i18n/useFormat';
import { formatCountdown } from '@/i18n/format';
import { paths } from '@/app/paths';
import { wallet, useBalancePolicy, type BalancePolicy } from '@/wallet/wallet';
import { fx, fxConfig, sound, haptic, useCelebration, D } from '@/fx';
import { Button, CloseButton, Coin, Dialog, Disclaimer, cx, useIsMobile } from '@/ui';
import { MiscArt } from '@/art/MiscArt';
import { useNow } from '@/features/lobby/useLobbyLive';
import { useCulture } from '@/i18n/culture';
import type { EnvelopeState } from '@/art/envelope';
import { EnvelopeArt } from './Envelope';
import { useDailyBonus } from './useDailyBonus';
import s from './bonus.module.css';

type DayState = 'done' | 'cur' | 'next';

export function dayState(n: number, day: number, claimableNow: boolean): DayState {
  if (n < day) return 'done';
  if (n === day) return claimableNow ? 'cur' : 'done';
  return 'next';
}

/** Red envelope per day (culture cn): claimed / past → open, claimable today → sealed, future → closed. */
export function envelopeState(n: number, day: number, claimableNow: boolean): EnvelopeState {
  const st = dayState(n, day, claimableNow);
  return st === 'done' ? 'open' : st === 'cur' ? 'sealed' : 'closed';
}

const FLIP: Keyframe[] = [{ transform: 'rotateY(0)' }, { transform: 'rotateY(90deg)' }, { transform: 'rotateY(0) scale(1.05)' }, { transform: 'scale(1)' }];
const OPEN: Keyframe[] = [{ transform: 'scale(1)' }, { transform: 'scale(1.08) rotate(-4deg)' }, { transform: 'scale(1.08) rotate(4deg)' }, { transform: 'scale(1)' }];

/** Daily bonus: 7-day calendar (design/system "03 Daily bonus"). Modal on desktop, bottom sheet on phones. */
export function DailyBonusModal() {
  const open = useSession((x) => x.dailyOpen);
  const setOpen = useSession((x) => x.openDaily);
  const status = useSession((x) => x.status);
  const { state, claiming, load, claim } = useDailyBonus();
  const intl = useIntl();
  const f = useFormat();
  const l = useLocale();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const titleId = useId();
  const boardRef = useRef<HTMLDivElement>(null);
  const cel = useCelebration();
  const now = useNow(1000);
  const cn = useCulture() === 'cn';

  useEffect(() => {
    if (open) void load();
  }, [open, status, load]);

  // while the dialog is open, a daily-bonus credit waits for the card animation (coins fly from the card)
  const policy = useCallback<BalancePolicy>((e) => (e.reason === 'daily_bonus' ? { mode: 'hold' } : undefined), []);
  useBalancePolicy(open ? policy : noPolicy);

  const close = useCallback(() => {
    cel.skip();
    setOpen(false);
  }, [cel, setOpen]);

  const onClaim = async () => {
    if (status !== 'user') {
      setOpen(false);
      navigate(paths.signIn(l));
      return;
    }
    const card = boardRef.current?.querySelector<HTMLElement>(`[data-day="${state.day}"]`) ?? null;
    let res;
    try {
      res = await claim();
    } catch (e) {
      toast.error(e);
      return;
    }
    await wallet.receive({ balance: res.balance, delta: res.amount, reason: 'daily_bonus' });
    void cel.run(async (signal) => {
      sound.play('common/bonus-open', { signal });
      haptic('medium');
      // reduced motion: no flip / opening animation at all (the result is shown at once)
      if (card && !signal.aborted && !fxConfig.reduced) {
        await card
          .animate(cn ? OPEN : FLIP, { duration: D(cn ? 520 : 380), easing: 'ease-out' })
          .finished.catch(() => undefined);
      }
      if (signal.aborted) {
        await wallet.reveal();
        return;
      }
      if (cn && card && boardRef.current) {
        const bb = boardRef.current.getBoundingClientRect();
        const cr = card.getBoundingClientRect();
        fx.burst(boardRef.current, cr.left - bb.left + cr.width / 2, cr.top - bb.top + cr.height / 2, 12, 'var(--gold)');
      }
      // the coin stream of the reveal is the win sound here (bonus-open already rang)
      await wallet.reveal({ from: card, celebrate: true, coins: 10 });
      if (!signal.aborted) fx.fireworks(3, boardRef.current);
    });
  };

  const stateLine = intl.formatMessage({ id: 'bonus.progress' }, { day: state.day, streak: state.streak });
  const reward = state.rewards[state.day - 1] ?? 0;
  const nextIn = state.nextClaimAt ? Math.max(0, new Date(state.nextClaimAt).getTime() - now) : 0;

  const dayCards = (
    <div className={mobile ? s.mGrid : s.grid}>
      {state.rewards.map((v, i) => {
        const n = i + 1;
        const st = dayState(n, state.day, state.claimableNow);
        const last = n === 7;
        return (
          <div
            key={n}
            data-day={n}
            className={cx(s.day, st === 'done' && s.done, st === 'cur' && s.cur, st === 'next' && last && s.last)}
            aria-current={st === 'cur' ? 'date' : undefined}
          >
            <span className={s.dayLabel}>
              <FormattedMessage id="bonus.day" values={{ n }} />
            </span>
            <span className={s.dayCoin} aria-hidden="true">
              {st === 'done' ? '✓' : last ? <MiscArt name="gift" size={30} /> : <Coin size={26} />}
            </span>
            <span className={s.dayValue}>{f.int(v)}</span>
          </div>
        );
      })}
    </div>
  );

  const envelopes = (
    <div className={mobile ? s.mEnvGrid : s.envGrid}>
      {state.rewards.map((v, i) => {
        const n = i + 1;
        const env = envelopeState(n, state.day, state.claimableNow);
        return (
          <div
            key={n}
            data-day={n}
            data-envelope={env}
            role="img"
            aria-label={intl.formatMessage({ id: `bonus.envelope.${env}` }, { n, amount: f.int(v), num: v })}
            aria-current={env === 'sealed' ? 'date' : undefined}
            className={cx(s.env, env === 'sealed' && s.envSealed, env === 'open' && s.envOpen)}
          >
            <span className={s.dayLabel}>
              <FormattedMessage id="bonus.day" values={{ n }} />
            </span>
            <EnvelopeArt state={env} width={mobile ? 40 : 60} />
            <span className={cx(s.envValue, env === 'closed' && s.envMuted)}>{f.int(v)}</span>
          </div>
        );
      })}
    </div>
  );
  const cards = cn ? envelopes : dayCards;

  const claimBtn =
    status !== 'user' ? (
      <Button tone="orange" size="lg" minWidth={260} block={mobile} onClick={onClaim}>
        <FormattedMessage id="bonus.signInToClaim" />
      </Button>
    ) : state.claimableNow ? (
      <Button tone="orange" size="lg" minWidth={260} block={mobile} onClick={onClaim} disabled={claiming} data-fx="claim">
        {cn ? (
          <FormattedMessage id="bonus.envelope.openCta" values={{ amount: f.int(reward) }} />
        ) : (
          <FormattedMessage id="bonus.claim" values={{ amount: f.int(reward), num: reward }} />
        )}
      </Button>
    ) : (
      <Button tone="orange" variant="outline" size="lg" minWidth={260} block={mobile} disabled>
        {state.nextClaimAt ? (
          <FormattedMessage id="bonus.nextIn" values={{ time: formatCountdown(nextIn) }} />
        ) : (
          <FormattedMessage id="bonus.claimed" />
        )}
      </Button>
    );

  return (
    <Dialog open={open} onClose={close} labelledBy={titleId} width={760}>
      <div ref={boardRef}>
        {mobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h2 id={titleId} className={s.mTitle}>
                <FormattedMessage id="bonus.title" />
              </h2>
              <p className={s.mSub}>{stateLine}</p>
            </div>
            {cards}
            {claimBtn}
            {status !== 'user' && (
              <p className={s.guestNote} style={{ margin: 0 }}>
                <FormattedMessage id="bonus.subtitle" />
              </p>
            )}
            <Disclaimer privacy={false} />
          </div>
        ) : (
          <>
            <div className={s.head}>
              <div className={s.titles}>
                <h2 id={titleId} className={s.title}>
                  <FormattedMessage id="bonus.title" />
                </h2>
                <p className={s.sub}>
                  <FormattedMessage id="bonus.subtitle" />
                </p>
              </div>
              <CloseButton onClick={close} />
            </div>
            {cards}
            <div className={s.foot}>
              <span className={s.progress}>{stateLine}</span>
              {claimBtn}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

const noPolicy: BalancePolicy = () => undefined;
